/**
 * Code 128-B em SVG, sem biblioteca.
 *
 * Por que à mão: a etiqueta precisa de um código que o coletor leia, e as
 * bibliotecas do ramo desenham em `<canvas>` — que imprime borrado, porque o
 * canvas é bitmap na resolução da TELA (~96 dpi) e a impressora térmica é de
 * 203 ou 300 dpi. SVG é vetor: a barra sai com a borda que a impressora
 * conseguir dar, e não com a que o monitor tinha.
 *
 * São ~40 linhas de tabela e uma soma de verificação. A alternativa era mais
 * um pacote no bundle para resolver isso pior.
 */

/**
 * Os 107 padrões do Code 128, cada um com as larguras de barra/espaço
 * alternadas (barra primeiro). Índice = valor do símbolo; 106 é o STOP, que
 * tem uma barra a mais.
 */
const PADROES = [
  '212222',
  '222122',
  '222221',
  '121223',
  '121322',
  '131222',
  '122213',
  '122312',
  '132212',
  '221213',
  '221312',
  '231212',
  '112232',
  '122132',
  '122231',
  '113222',
  '123122',
  '123221',
  '223211',
  '221132',
  '221231',
  '213212',
  '223112',
  '312131',
  '311222',
  '321122',
  '321221',
  '312212',
  '322112',
  '322211',
  '212123',
  '212321',
  '232121',
  '111323',
  '131123',
  '131321',
  '112313',
  '132113',
  '132311',
  '211313',
  '231113',
  '231311',
  '112133',
  '112331',
  '132131',
  '113123',
  '113321',
  '133121',
  '313121',
  '211331',
  '231131',
  '213113',
  '213311',
  '213131',
  '311123',
  '311321',
  '331121',
  '312113',
  '312311',
  '332111',
  '314111',
  '221411',
  '431111',
  '111224',
  '111422',
  '121124',
  '121421',
  '141122',
  '141221',
  '112214',
  '112412',
  '122114',
  '122411',
  '142112',
  '142211',
  '241211',
  '221114',
  '413111',
  '241112',
  '134111',
  '111242',
  '121142',
  '121241',
  '114212',
  '124112',
  '124211',
  '411212',
  '421112',
  '421211',
  '212141',
  '214121',
  '412121',
  '111143',
  '111341',
  '131141',
  '114113',
  '114311',
  '411113',
  '411311',
  '113141',
  '114131',
  '311141',
  '411131',
  '211412',
  '211214',
  '211232',
  '2331112',
] as const;

const INICIO_B = 104;
const INICIO_C = 105;
/** Dentro do conjunto C, troca para o B (para o último dígito de número ímpar). */
const PARA_B = 100;
const PARADA = 106;

/**
 * @param texto conteúdo do código. Fora de ASCII 32–126 não existe em 128-B;
 *   caractere de fora é descartado, porque imprimir um código que o leitor
 *   recusa é pior do que imprimir um código mais curto.
 */
function modulos(texto: string): number[] {
  const validos = [...texto].filter((c) => {
    const n = c.charCodeAt(0);
    return n >= 32 && n <= 126;
  });

  const valores = validos.map((c) => c.charCodeAt(0) - 32);
  // Soma ponderada pela posição (1-based), com o START entrando como peso 1.
  const soma = valores.reduce((acc, v, i) => acc + v * (i + 1), INICIO_B);
  const simbolos = [INICIO_B, ...valores, soma % 103, PARADA];

  const larguras: number[] = [];
  for (const s of simbolos) {
    for (const d of PADROES[s] ?? PADROES[0]) larguras.push(Number(d));
  }
  return larguras;
}

/**
 * Code 128-C: dois dígitos por símbolo — metade da largura do 128-B.
 *
 * É o que o ReportBuilder faz com `AutoEncode` na etiqueta de expedição: a
 * chave da NF-e tem 44 dígitos, e em 128-B ela não cabe na etiqueta com barra
 * que a térmica consiga imprimir. Número de tamanho ímpar fecha com o último
 * dígito no conjunto B.
 */
function modulosNumericos(digitos: string): number[] {
  const pares = digitos.length - (digitos.length % 2);
  const valores: number[] = [];
  for (let i = 0; i < pares; i += 2) valores.push(Number(digitos.slice(i, i + 2)));
  if (pares < digitos.length) valores.push(PARA_B, digitos.charCodeAt(pares) - 32);

  const soma = valores.reduce((acc, v, i) => acc + v * (i + 1), INICIO_C);
  const simbolos = [INICIO_C, ...valores, soma % 103, PARADA];

  const larguras: number[] = [];
  for (const s of simbolos) {
    for (const d of PADROES[s] ?? PADROES[0]) larguras.push(Number(d));
  }
  return larguras;
}

export function CodigoBarras({
  texto,
  alturaPt = 27.7,
  larguraPt = 179.5,
  moduloMm,
  className,
}: {
  texto: string;
  /**
   * Medidas em PONTOS, e não em milímetros, porque vieram medidas do PDF que
   * o Procfit gera: o barcode da etiqueta ocupa 179,5 × 27,7 pt (≈ 63 × 9,8 mm).
   */
  alturaPt?: number;
  /**
   * A largura MANDA: o módulo é calculado para o código caber nela.
   *
   * Fixar a largura do módulo era o erro anterior — 13 dígitos em Code 128
   * dão 178 módulos, que a 0,4 mm viram 71 mm e estouravam a coluna de 62 mm,
   * cortando o código pela direita. Código cortado não é código ruim: é
   * código que não lê.
   */
  larguraPt?: number;
  /**
   * Largura de UM módulo em mm. Quando informada, ela manda e a largura total
   * sai do código — como no ReportBuilder (`mmBarWidth`), em que o código
   * cresce para a direita. Só-dígitos vai em 128-C.
   */
  moduloMm?: number;
  className?: string | undefined;
}): JSX.Element | null {
  const limpo = texto?.trim() ?? '';
  if (!limpo) return null;

  const numerico = moduloMm !== undefined && /^\d{2,}$/.test(limpo);
  const larguras = numerico ? modulosNumericos(limpo) : modulos(limpo);
  const total = larguras.reduce((a, l) => a + l, 0);
  const largura = moduloMm !== undefined ? `${(total * moduloMm).toFixed(2)}mm` : `${larguraPt.toFixed(2)}pt`;

  const barras: { x: number; largura: number }[] = [];
  let x = 0;
  larguras.forEach((l, i) => {
    // Índice par = barra, ímpar = espaço.
    if (i % 2 === 0) barras.push({ x, largura: l });
    x += l;
  });

  return (
    <svg
      className={className}
      width={largura}
      height={`${alturaPt.toFixed(2)}pt`}
      viewBox={`0 0 ${total} 100`}
      preserveAspectRatio="none"
      // Sem anti-aliasing: borda de barra cinza vira ponto falhado na térmica.
      shapeRendering="crispEdges"
      role="img"
      aria-label={`Código de barras ${limpo}`}
    >
      {barras.map((b) => (
        <rect key={b.x} x={b.x} y={0} width={b.largura} height={100} fill="#000" />
      ))}
    </svg>
  );
}
